/**
 * @fileoverview The main content section of the application, which contains the spinner itself.
 *
 * This component is responsible for rendering the spinner, handling all the spin logic,
 * calculating the winner, displaying animations, and managing the responsive layout
 * of the spinner area.
 */

import * as React from "react";
import { useRef, useState, useEffect, useCallback, useLayoutEffect, useMemo } from "react";
import { useSpinner, colorOptions, colorSkinOptions, colorEyesOptions, colorHairOptions } from "../../../../lib/SpinnerContext";
import { useParticipants, Participant } from "../../../../lib/ParticipantsContext";
import { useWheel } from "../../../../lib/WheelContext";
import { useSettings } from "../../../../lib/SettingsContext";
import Spinner from "../../../../components/Spinner/Spinner";
import { SidebarMode } from "../../../../lib/MenuItems";

import './MainContentSection.css';
import './WinnerNameEffect.css';
import { Modal } from '../../../../components/ui/modal';
import AdManager from '../../../../components/ads/AdManager';
import { Pencil } from 'lucide-react';
import { useTheme } from '../../../../lib/ThemeContext';
// import { APP_PROPERTIES } from '../../../../config/appProperties';
import { useGame } from "../../../../lib/GameContext";
import WouldYouRatherGameCard from "../../../../components/ui/WouldYouRatherGameCard";
import WhichOneYouAreCard from "../../../../components/ui/WhichOneYouAreCard";
import TriviaGameCard from "../../../../components/ui/TriviaGameCard";

import TruthDareCard from "../../../../components/ui/TruthDareCard";
// Removed app-load preloader per request to avoid early loading
import { useSidebar } from '../../../../lib/SidebarContext';
import { useLanguage } from '../../../../lib/LanguageContext';
import { getEnvironmentInfo } from '../../../../config/version';
import { useAuth } from '../../../../context/AuthContext';
import { GameQuestionManager } from '../../../../services/GameQuestionManager';
import { FirestoreQuestionProgressPersistence } from '../../../../services/QuestionProgressPersistence';
import LandingPageContent from '../../../../components/LandingPageContent';



// Augment CSSProperties to allow custom property for dice rotation
declare module 'react' {
  interface CSSProperties {
    '--rotation'?: string;
    'WebkitUserDrag'?: string; // Add WebkitUserDrag for Safari image drag prevention
    'WebkitTransform'?: string; // Add WebkitTransform for iOS optimizations
    'WebkitBackfaceVisibility'?: string; // Add WebkitBackfaceVisibility for iOS optimizations
    'WebkitTransition'?: string; // Add WebkitTransition for iOS optimizations
  }
}

/**
 * Safari-specific detection utility
 */
const isSafari = () => {
  if (typeof window === 'undefined') return false;
  const userAgent = navigator.userAgent;
  return /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
};

/**
 * Calculates which participant segment is highlighted by the spinner's pointer.
 * This is primarily used for spinners where a static pointer indicates the winner.
 * @param {number} rotation - The current rotation angle of the spinner in degrees.
 * @param {number} participantCount - The total number of participants on the wheel.
 * @returns {number} The index of the highlighted participant.
 */
const getHighlightedParticipant = (rotation: number, participantCount: number): number => {
  // Normalize the rotation to ensure it's between 0-360
  let normalizedRotation = ((rotation % 360) + 360) % 360;
  
  // Calculate the segment size
  const segmentSize = 360 / participantCount;
  
  // For bottle spinners, we need to adjust the angle
  // The bottle neck points up (which is 270° in a system where 0° is right and angles increase CCW)
  // normalizedRotation is the CSS rotation (clockwise positive)
  // So, pointerGlobalAngle is the actual direction the neck points in the 0-deg-right CCW system.
  const pointerGlobalAngle = (normalizedRotation + 270) % 360;
  
  // Participants are laid out with participant 0 centered at -90 degrees (or 270 degrees).
  // Participant idx is centered at (idx * segmentSize - 90).
  // The segment for participant idx starts at (idx * segmentSize - 90 - segmentSize / 2).
  // We want to find idx such that:
  // idx * segmentSize - 90 - segmentSize / 2  <= pointerGlobalAngle < idx * segmentSize - 90 + segmentSize / 2
  // Adding (90 + segmentSize / 2) to all parts:
  // idx * segmentSize <= pointerGlobalAngle + 90 + segmentSize / 2 < (idx + 1) * segmentSize
  // So, idx = floor((pointerGlobalAngle + 90 + segmentSize / 2) / segmentSize)
  // The result of (pointerGlobalAngle + 90 + segmentSize / 2) should be taken modulo 360 before division
  // to handle wraparound correctly and ensure the dividend is in a known range for floor.

  let effectiveAngle = pointerGlobalAngle + 90 + segmentSize / 2;
  effectiveAngle = ((effectiveAngle % 360) + 360) % 360; // Normalize to [0, 360)

  const highlightedParticipant = Math.floor(effectiveAngle / segmentSize);
  
  // Handle edge cases when calculation might go out of bounds or participantCount is 0
  if (participantCount === 0) return 0; // Should not happen if called after checks
  return highlightedParticipant >= participantCount 
    ? participantCount -1 // Should ideally not happen if effectiveAngle is in [0,360)
    : highlightedParticipant;
};

/**
 * Props for the MainContentSection component.
 */
interface MainContentSectionProps {
  /** Indicates if the sidebar is in its collapsed state. */
  sidebarCollapsed?: boolean;
  /** Flag indicating if the screen is small (mobile view). */
  isSmallScreen?: boolean;
  /** A ref to the sidebar component to access its methods. */
  sidebarRef?: React.RefObject<any>;
      /** Callback to add a new result to the history. */
    addSpinResult: (winner: string, type: SidebarMode, spinnerName: string, gameType?: string, gameResultCandidate?: string) => void;
  /** The current mode of the sidebar (e.g., 'participants', 'numbers'). */
  mode?: SidebarMode;
  /** The actual sidebar tab (e.g., 'games', 'spinner-styles', etc.) */
  sidebarMode?: SidebarMode;
  /** The current name of the spinner. */
  spinnerName: string;
  /** Callback to set the name of the spinner. */
  setSpinnerName: (name: string) => void;
}

/**
 * The main component that renders the spinner and manages its state and animations.
 * @param {MainContentSectionProps} props - The props for the component.
 * @returns {JSX.Element} The rendered main content section.
 */
export const MainContentSection = ({ sidebarCollapsed, isSmallScreen, sidebarRef, addSpinResult, mode, sidebarMode, spinnerName, setSpinnerName }: MainContentSectionProps & { spinnerName: string, setSpinnerName: (name: string) => void }) => {
  const { selectedSpinner, spinRequested, setSpinRequested, setIsSidebarLocked } = useSpinner();
  const { removeParticipant } = useParticipants();
  const { wheelItems } = useWheel();
  const { isDarkMode } = useTheme();
  const { currentGameType } = useGame();
  const { currentLanguage } = useLanguage();
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [winnerIndex, setWinnerIndex] = useState<number | null>(null);
  const [isCelebrating, setIsCelebrating] = useState(false);
  const slowSpinRef = useRef<number | null>(null);
  const slowSpinLastTime = useRef(performance.now());
  const applauseSound = '/sounds/applause.mp3';
  const spinSoundRef = useRef<HTMLAudioElement | null>(null);
  const { shouldRemoveParticipant, audioEnabled } = useSettings();
  const spinParticipantsRef = useRef<Participant[]>([]);
  const finalWinnerRef = useRef<{ name: string; type: SidebarMode } | null>(null);
  const spinResultRecordedRef = useRef(false);
  const [isDiceMode, setIsDiceMode] = useState(false);
  const [isBinaryMode, setIsBinaryMode] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [pendingName, setPendingName] = useState('');
  const [spinElapsed, setSpinElapsed] = useState<number | null>(null);
  const [showGameCard, setShowGameCard] = useState(false);
  const [gameCardWinnerName, setGameCardWinnerName] = useState<string>('');
  const [gameCardShouldPersist, setGameCardShouldPersist] = useState(false);
  const [removingWinner, setRemovingWinner] = useState(false);
  
  // Track timeout IDs for cleanup to prevent fake results
  const timeoutRefs = useRef<{
    celebrationTimeout?: NodeJS.Timeout;
    resultTimeout?: NodeJS.Timeout;
    bufferTimeout?: NodeJS.Timeout;
    initialTimeout?: NodeJS.Timeout;
  }>({});
  
  // Development guard to prevent fake results
  const isDevelopment = process.env.NODE_ENV === 'development';
  const lastSpinTimeRef = useRef<number>(0);
  const winnerRef = useRef<Participant | null>(null);
  const finalRotationRef = useRef<number | null>(null);
  const winnerAnimationTriggeredRef = useRef(false); // Safari-specific guard to prevent duplicate animations
  const spinnerSettledRef = useRef(false); // Track when spinner has completely settled
  const { numbers, randomNumbers, colors, colorType, removeNumber, removeRandomNumber, removeColor } = useSidebar();
  // Footer is no longer fixed, so no need to track its height
  const { user } = useAuth();

  // Constants for layout calculation
  const SIDEBAR_WIDTH = 256;
  const SIDEBAR_WIDTH_COLLAPSED = 80;
  const HEADER_HEIGHT = 54; // Fixed header height
  const FOOTER_HEIGHT = 0; // Footer is not rendered on desktop; reserve no space

  const [calculatedRedBoxDimension, setCalculatedRedBoxDimension] = useState(600);

  // --- DRAG BAR STATE ---
  const [numberCount, setNumberCount] = useState(6); // default 6
  const [colorCount, setColorCount] = useState(6); // default 6
  const [randomNumberCount, setRandomNumberCount] = useState(6); // default 6

  // --- BINARY ---
  // We'll get binaryList from the sidebar context
  const { binaryList } = useSidebar();
  const binary = useMemo(() => 
    binaryList.map((name: string, index: number) => ({
      id: `binary-${index}`,
      name
    })), [binaryList]
  );

  // Helper to get the active list and removal function based on menu mode
  function getActiveListAndRemover(
    mode: string,
    wheelItems: Participant[],
    removeParticipant: (id: string) => void
  ): { list: Participant[]; remove: (id: string) => void } {
    switch (mode) {
      case 'numbers':
        return { list: numbers, remove: removeNumber };
      case 'random-numbers':
        return { list: randomNumbers, remove: removeRandomNumber };
      case 'colors':
        return { list: colors, remove: removeColor };
      case 'binary':
        return { list: binary, remove: removeParticipant };
      default:
        return { list: wheelItems, remove: removeParticipant };
    }
  }

  useLayoutEffect(() => {
    function updateDimensions() {
      let availableContentWidth = window.innerWidth;
      if (!isSmallScreen) {
        availableContentWidth -= sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;
      }

      const viewportEffectiveHeight = window.innerHeight - HEADER_HEIGHT;
      // For mobile, use full width, for desktop use 90% of available width
      const widthMultiplier = isSmallScreen ? 1 : 0.9;
      let viewportBasedTargetSize = Math.min(availableContentWidth * widthMultiplier, viewportEffectiveHeight * 0.9);

      // The final dimension is the smaller of the viewport-based target and what can fit in the available width
      const finalDim = Math.min(viewportBasedTargetSize, availableContentWidth * widthMultiplier);
      setCalculatedRedBoxDimension(finalDim > 0 ? finalDim : 300); // Ensure a minimum size
    }

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [isSmallScreen, sidebarCollapsed]);

  // Footer is no longer fixed, so no need to measure its height

  // Add inner padding for spinner elements
  const INNER_PADDING = Math.max(24, calculatedRedBoxDimension * 0.05); // 5% or at least 24px
  const paddedDimension = calculatedRedBoxDimension - INNER_PADDING * 0.1;

  const isMobile = window.innerWidth < 1024; // Tablets now use mobile behavior
  const isDesktop = window.innerWidth >= 1024;

  // Spinner element sizing: only reduce on mobile
  const spinnerDimension = isMobile ? paddedDimension * 0.9 : paddedDimension;

  // Calculate available height for red box (between header and bottom)
  const availableHeight = window.innerHeight - HEADER_HEIGHT;
  const availableWidth = window.innerWidth - (sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH);
  const desktopRedBoxSize = Math.min(availableHeight, availableWidth * 0.9);

  const redBoxStyle = isDesktop
      ? {
          width: '100%',
          height: `${calculatedRedBoxDimension}px`,
          position: 'relative' as const,
          display: 'flex',
          flexDirection: 'column' as const,
          alignItems: 'center' as const,
          justifyContent: 'flex-start',
          backgroundColor: 'transparent',
          border: '2px solid transparent',
          borderRadius: '0px',
          overflow: 'visible',
          margin: '0',
          padding: '0',
        }
      : {
          width: '100vw',
          maxWidth: '100vw',
          height: `${calculatedRedBoxDimension}px`,
          position: 'relative' as const,
          display: 'flex',
          flexDirection: 'column' as const,
          alignItems: 'center' as const,
          justifyContent: 'center',
          backgroundColor: 'transparent',
          border: '2px solid transparent',
          borderRadius: '8px',
          overflow: 'visible',
          margin: '0',
          padding: '0',
        };

  // Parent flex centering for desktop - fill available height provided by Frame
  const parentFlexStyle = isDesktop
    ? { width: '100%', padding: '0', overflow: 'visible' }
    : { overflow: 'visible' };

  const spinnerContainerStyle = {
    width: '100%',
    height: '100%',
    position: 'relative' as const,
    overflow: 'visible',
  };

  // Add a helper to detect binary mode
  useEffect(() => {
    setIsBinaryMode(wheelItems.length === 2 && (
      (wheelItems[0].name === 'True' && wheelItems[1].name === 'False') ||
      (wheelItems[0].name === 'Right' && wheelItems[1].name === 'Wrong') ||
      (wheelItems[0].name === 'Yes' && wheelItems[1].name === 'No')
    ));
  }, [wheelItems]);

  // Add helper to check if in dice mode
  useEffect(() => {
    setIsDiceMode(wheelItems.length > 0 && wheelItems.every(p => ['⚀', '⚁', '⚂', '⚃', '⚄', '⚅'].includes(p.name)));
  }, [wheelItems]);

  // Manage game card persistence when switching modes
  useEffect(() => {
    // If we're not in participants mode and game card should persist, keep it visible
    if (mode !== 'participants' && gameCardShouldPersist && (currentGameType === 'would-you-rather' || currentGameType === 'which-one-you-are' || currentGameType === 'trivia')) {
      // Keep the game card visible when switching to other sub-menus
      // but don't show new game cards for non-participant modes
    } else if (mode !== 'participants' && !gameCardShouldPersist) {
      // If we're not in participants mode and game card shouldn't persist, hide it
      setShowGameCard(false);
    }
  }, [mode, gameCardShouldPersist, currentGameType]);

  // Reset game card persistence when switching away from Fun Modes
  useEffect(() => {
    if (currentGameType !== 'would-you-rather' && currentGameType !== 'which-one-you-are' && currentGameType !== 'trivia' && currentGameType !== 'truth-dare') {
      setShowGameCard(false);
      setGameCardShouldPersist(false);
    }
  }, [currentGameType]);

  // Initialize spin sound
  useEffect(() => {
    if (selectedSpinner.sound) {
      spinSoundRef.current = new Audio(selectedSpinner.sound);
      spinSoundRef.current.loop = false; // Play once, don't repeat
    }
    return () => {
      if (spinSoundRef.current) {
        spinSoundRef.current.pause();
        spinSoundRef.current = null;
      }
    };
  }, [selectedSpinner.sound]);

  // --- Continuous slow spinning logic ---
  const animateSlowSpin = useCallback((now: number) => {
    const delta = now - slowSpinLastTime.current;
    slowSpinLastTime.current = now;
    
    setRotation(r => {
      const next = r + 0.02 * delta;
      return Math.round(next * 100) / 100; // Reduce jitter
    });
    slowSpinRef.current = requestAnimationFrame(animateSlowSpin);
  }, []); // No dependencies, setRotation is stable

  const startSlowSpinAnimation = useCallback(() => {
    if (slowSpinRef.current) cancelAnimationFrame(slowSpinRef.current); // Ensure no duplicates
    slowSpinLastTime.current = performance.now(); // Reset time to prevent jump
    slowSpinRef.current = requestAnimationFrame(animateSlowSpin);
  }, [animateSlowSpin]);

  const stopSlowSpinAnimation = useCallback(() => {
    if (slowSpinRef.current) {
      cancelAnimationFrame(slowSpinRef.current);
      slowSpinRef.current = null;
    }
  }, []);

  // Effect for managing slow spin based on game state and visibility
  useEffect(() => {
    const conditionsMet = !spinning && !isCelebrating;
    if (conditionsMet && document.visibilityState === 'visible') {
      startSlowSpinAnimation();
    } else {
      stopSlowSpinAnimation();
    }
    // Cleanup on unmount or when dependencies change significantly enough to stop/restart
    return stopSlowSpinAnimation;
  }, [selectedSpinner.id, spinning, isCelebrating, startSlowSpinAnimation, stopSlowSpinAnimation]);

  // Effect for handling page visibility changes specifically for slow spin
  useEffect(() => {
    const handleVisibilityChange = () => {
      const conditionsMet = !spinning && !isCelebrating;
      if (document.hidden) {
        stopSlowSpinAnimation();
      } else if (conditionsMet) {
        startSlowSpinAnimation();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopSlowSpinAnimation(); // Also stop if component unmounts while listener is active
    };
  }, [spinning, isCelebrating, startSlowSpinAnimation, stopSlowSpinAnimation]);

  // Effect for click-to-spin
  useEffect(() => {
    if (spinRequested) {
      handleSpin();
      setSpinRequested(false); // Reset the request
    }
  }, [spinRequested]);

  // Separate effect for winner index reset only
  useEffect(() => {
    if (!spinning && !isCelebrating) {
      setWinnerIndex(null);
    }
  }, [spinning, isCelebrating]);

  // Cleanup effect for Safari-specific guards and refs
  useEffect(() => {
    return () => {
      // Cleanup on component unmount
      winnerAnimationTriggeredRef.current = false;
      winnerRef.current = null;
      finalRotationRef.current = null;
      spinnerSettledRef.current = false;
      if (slowSpinRef.current) {
        cancelAnimationFrame(slowSpinRef.current);
        slowSpinRef.current = null;
      }
    };
  }, []);

  // Load spinner name from saved/shared spinner if available
  useEffect(() => {
    // Example: if you have a prop or context for loaded spinner data
    // setSpinnerName(loadedSpinner?.name || '');
  }, []);

  // Use helper to get the correct list and removal function
  const { list: activeList, remove: removeActive } = getActiveListAndRemover(
    mode || 'participants',
    wheelItems,
    removeParticipant
  );

  // --- Spin logic ---
  
  // Preload questions for the current game type during spin
  const preloadQuestions = useCallback(() => {
    // Only load questions if:
    // 1. We have a game type selected (not just-spinner)
    // 2. We're in participants mode (not colors/numbers/binary/dice)
    // 3. We have actual participants to spin
    if (!currentGameType || 
        currentGameType === 'just-spinner' || 
        mode !== 'participants' || 
        activeList.length === 0) {
      return;
    }
    
    // Don't preload on every spin - only when the game card is shown
    // The GameQuestionManager will handle prefetching automatically at the right time
    console.log(`🚀 Skipping preload for ${currentGameType} - will load when game card is shown`);
  }, [currentGameType, currentLanguage, user?.uid, mode, activeList]);

  const handleSpin = () => {
    if (spinning || isCelebrating || activeList.length === 0 || showGameCard) return;
    
    // At the very start of the spin, snapshot the current active list (menu-specific list)
    spinParticipantsRef.current = activeList.map(p => ({ ...p }));
    spinResultRecordedRef.current = false; // Reset the result recording flag

    // Preload questions for the current game type (only for participants mode)
    if (mode === 'participants') {
      preloadQuestions();
    }

    // Call sidebar's imperative handle to collapse it
    if(sidebarRef?.current?.collapseSidebar) {
      sidebarRef.current.collapseSidebar();
    }
    
    doSpin();
  };

  const doSpin = () => {
    // Clean up any previous spin state and cache
    if (slowSpinRef.current) {
      cancelAnimationFrame(slowSpinRef.current);
      slowSpinRef.current = null;
    }
    
    // CRITICAL FIX: Clear all previous timeouts to prevent fake results
    if (timeoutRefs.current.celebrationTimeout) {
      clearTimeout(timeoutRefs.current.celebrationTimeout);
      timeoutRefs.current.celebrationTimeout = undefined;
    }
    if (timeoutRefs.current.resultTimeout) {
      clearTimeout(timeoutRefs.current.resultTimeout);
      timeoutRefs.current.resultTimeout = undefined;
    }
    if (timeoutRefs.current.bufferTimeout) {
      clearTimeout(timeoutRefs.current.bufferTimeout);
      timeoutRefs.current.bufferTimeout = undefined;
    }
    if (timeoutRefs.current.initialTimeout) {
      clearTimeout(timeoutRefs.current.initialTimeout);
      timeoutRefs.current.initialTimeout = undefined;
    }
    

    
    // Reset all spin-related state
    setSpinElapsed(null);
    setWinnerIndex(null);
    finalRotationRef.current = null; // Reset final rotation ref
    winnerAnimationTriggeredRef.current = false; // Reset Safari-specific guard
    spinnerSettledRef.current = false; // Reset settled state
    
    // Force a small delay to clear any residual animations
    setTimeout(() => {
      setIsSidebarLocked(true);
      setSpinning(true);
      setWinnerIndex(null);

      const startTime = performance.now();
      const rapidDuration = 6000; // 5s rapid phase (Step 1: Rapid spin)
      const slowDuration = 8000; // 8s natural deceleration for smoother stop (Step 2: Slow down spin)
      const totalDuration = rapidDuration + slowDuration;

      // Use crypto.getRandomValues for true randomness
      const randomBuffer = new Uint32Array(3);
      window.crypto.getRandomValues(randomBuffer);
      // 3–8 extra spins (further reduced for smoother feel)
      const randomExtraSpins = 3 + (randomBuffer[0] % 6);
      // Any angle between 0 and 360
      const randomOffset = (randomBuffer[1] / 0xffffffff) * 360;
      // No final position variation to eliminate any deflection
      const finalPositionVariation = 0; // No variation for perfect stop

      const rapidSpeed = 1600; // degrees per second (increased for faster rapid phase)
      const anglePer = 360 / activeList.length;

      // Calculate angles
      const angleRapid = rapidSpeed * (rapidDuration / 1400);
      const angleSlow = randomExtraSpins * 360 + randomOffset; // All extra spins and offset in deceleration
      const rapidPhaseRotation = rotation + angleRapid;
      const finalRotation = rapidPhaseRotation + angleSlow + finalPositionVariation;
      finalRotationRef.current = finalRotation; // Store final position for locking

      function animate(now: number) {
        const elapsed = now - startTime;
        setSpinElapsed(elapsed); // Update elapsed time for debug overlay
        
        if (elapsed < rapidDuration) {
          // Phase 1: Rapid, constant speed (like initial bottle spin)
          const t = elapsed / rapidDuration;
          const currentRotation = rotation + angleRapid * t;
          setRotation(currentRotation);
          if (spinSoundRef.current) {
            spinSoundRef.current.volume = 1;
          }
          requestAnimationFrame(animate);
        } else if (elapsed < totalDuration) {
          // Phase 2: Natural deceleration with smooth transition
          const t = (elapsed - rapidDuration) / slowDuration; // 0 to 1
          
          // Use a smooth easing curve that creates very gradual, graceful stop
          // This creates tiny increments at the end for a very smooth deceleration
          const ease = 1 - Math.pow(1 - t, 4); // Higher power for very gradual stop
          
          const currentRotation = rapidPhaseRotation + angleSlow * ease;
          setRotation(currentRotation);
          if (spinSoundRef.current) {
            spinSoundRef.current.volume = 1 - 0.8 * t;
          }
          requestAnimationFrame(animate);
        } else {
          // Final position - lock the position to prevent any post-stop movement
          const lockedRotation = finalRotationRef.current || finalRotation;
          setRotation(lockedRotation);
          setSpinElapsed(null); // Hide timer overlay after spin
          if (spinSoundRef.current) {
            spinSoundRef.current.pause();
          }
          // Remove the event listener that was added for this spin
          document.removeEventListener('visibilitychange', spinSoundVisibilityHandler);
          
          // Multiple position locks to ensure no movement
          // Safari-specific timing adjustment to prevent animation conflicts
          const safariDelay = isSafari() ? 150 : 100;
          setTimeout(() => {
            // First lock - ensure position is completely stable
            setRotation(lockedRotation);
            
            // Additional lock to guarantee final position
            setTimeout(() => {
              setRotation(lockedRotation);
              // Mark spinner as settled after final position lock
              spinnerSettledRef.current = true;
            }, 50);
            
            // Winner animation after position is locked and stable
            // Safari-specific timing adjustment for winner animation
            const winnerAnimationDelay = isSafari() ? 400 : 300;
            setTimeout(() => {
              // Only proceed if spinner is settled
              if (!spinnerSettledRef.current) {
                console.log('⚠️ Spinner not yet settled, waiting...');
                return;
              }
              // Safari-specific guard to prevent duplicate winner animations
              if (winnerAnimationTriggeredRef.current) {
                console.log('🦁 Safari: Winner animation already triggered, skipping duplicate');
                return;
              }
              
              winnerAnimationTriggeredRef.current = true;
              setSpinning(false);
              setIsCelebrating(true);
              if (audioEnabled) {
                const audio = new Audio(applauseSound);
                audio.play().catch(() => {});
              }
              const currentParticipants = spinParticipantsRef.current;
              // Use the locked final rotation to ensure winner is calculated from the final settled position
              // after the complete 2-step spin cycle (rapid spin + slow down spin)
              const finalLockedRotation = finalRotationRef.current || finalRotation;
              let winner;
              if (selectedSpinner.id === 'wheel') {
                // For wheel spinner, the triangle pointer points to the right (0 degrees)
                // We need to find which segment the pointer is pointing to
                const normalizedRotation = ((finalLockedRotation % 360) + 360) % 360;
                const trianglePointerAngle = 0; // Triangle points to the right (0 degrees)
                
                // Calculate which segment the pointer is pointing to
                // The pointer angle relative to the wheel's rotation
                const pointerRelativeAngle = (trianglePointerAngle - normalizedRotation + 360) % 360;
                
                // Each segment is anglePer degrees wide, starting from -90 degrees (top)
                // We need to find which segment contains the pointer angle
                const segmentStartAngle = -90; // First segment starts at -90 degrees (top)
                const adjustedAngle = (pointerRelativeAngle - segmentStartAngle + 360) % 360;
                winner = Math.floor(adjustedAngle / anglePer);
                
                // Ensure winner is within bounds
                if (winner < 0) winner += currentParticipants.length;
                winner = winner % currentParticipants.length;
              } else {
                winner = getHighlightedParticipant(finalLockedRotation, currentParticipants.length);
              }
              
              // Debug logging for winner calculation
              console.log(`🎯 Winner calculation:`, {
                finalRotation: finalRotation.toFixed(2),
                finalLockedRotation: finalLockedRotation.toFixed(2),
                winnerIndex: winner,
                winnerName: currentParticipants[winner]?.name,
                participantCount: currentParticipants.length,
                spinnerType: selectedSpinner.id
              });
              
              setWinnerIndex(winner);
              // Store the winner for later removal and animation
              const currentWinner = currentParticipants[winner];
              winnerRef.current = currentWinner;
              // --- Game card logic ---
              // Show game card only when:
              // 1. Current game type is 'would-you-rather', 'which-one-you-are', 'trivia', or 'truth-dare' (Fun Modes is active)
              // 2. Mode is 'participants' (actual participant names on wheel, not numbers/dice/colors/binary)
              // 3. But once shown, it persists when switching to other sub-menus (spinner-styles, fun-modes, etc.)
              if ((currentGameType === 'would-you-rather' || currentGameType === 'which-one-you-are' || currentGameType === 'trivia' || currentGameType === 'truth-dare') && mode === 'participants') {
                console.log(`🎮 Game card will be shown for ${currentGameType} with winner: ${currentWinner.name}`);
                console.log(`🎮 Debug - currentGameType: ${currentGameType}, mode: ${mode}, participants: ${currentParticipants.length}`);
                setTimeout(() => {
                  setGameCardWinnerName(currentWinner.name);
                  setShowGameCard(true);
                  setGameCardShouldPersist(true); // Mark that game card should persist across mode changes
                }, 3000); // 3 seconds after winner animation starts
              }
              timeoutRefs.current.resultTimeout = setTimeout(() => {
                setRemovingWinner(true);
                timeoutRefs.current.initialTimeout = setTimeout(() => {
                  // Record the spin result at the end of celebration (true winner confirmed)
                  if (winnerRef.current && addSpinResult && !spinResultRecordedRef.current) {
                    spinResultRecordedRef.current = true; // Mark as recorded to prevent duplicates
                    
                    // CRITICAL FIX: Add additional guard to prevent recursive calls
                    const now = Date.now();
                    const lastCallTime = (window as any).lastSpinResultTime || 0;
                    if (now - lastCallTime < 2000) {
                      console.log('🚫 BLOCKED recursive spin result call within 2 seconds');
                      return;
                    }
                    (window as any).lastSpinResultTime = now;
                    let winnerName = winnerRef.current.name;
                    let inferredType: SidebarMode = mode || 'participants';
                    
                    // Determine the type based on the current mode and participants
                    if (sidebarMode === 'spinner-styles') {
                      if (currentParticipants.length === 2) {
                        inferredType = 'binary';
                      } else if (currentParticipants.length > 0 && currentParticipants.every(p => ['⚀', '⚁', '⚂', '⚃', '⚄', '⚅'].includes(p.name))) {
                        inferredType = 'dice';
                      } else if (currentParticipants.length > 0 && currentParticipants.every(p => !isNaN(Number(p.name)))) {
                        inferredType = 'numbers';
                      } else if (currentParticipants.length > 0 && currentParticipants.every(p => colorOptions.some(c => c.name === p.name))) {
                        inferredType = 'colors';
                      } else {
                        inferredType = 'participants';
                      }
                    } else {
                      inferredType = mode || 'participants';
                    }
                    
                    // For colors, use the color key instead of name
                    if (inferredType === 'colors') {
                      const colorObj = colorOptions.find(c => c.name === winnerRef.current!.name);
                      if (colorObj) {
                        winnerName = colorObj.key;
                      }
                    }
                    
                    // Determine game type based on what's on the wheel
                    let gameType: string | undefined = undefined;
                    
                    // Check if the wheel has participants (not dice/colors/numbers/binary)
                    const hasParticipants = currentParticipants.length > 0 && 
                      !currentParticipants.every(p => ['⚀', '⚁', '⚂', '⚃', '⚄', '⚅'].includes(p.name)) && // not dice
                      !currentParticipants.every(p => !isNaN(Number(p.name))) && // not numbers
                      !currentParticipants.every(p => colorOptions.some(c => c.name === p.name)) && // not colors
                      !(currentParticipants.length >= 2 && currentParticipants.every(p => ['Yes', 'No', 'Heads', 'Tails', 'Stay', 'Go'].includes(p.name))); // not binary
                    
                    // If wheel has participants and a Fun Mode is selected (but not just-spinner), use that game type
                    if (hasParticipants && currentGameType && currentGameType !== 'just-spinner') {
                      gameType = currentGameType;
                    }
                    
                    // Record the result - only record non-game results, game cards handle their own completion
                    // Development guard to prevent fake results
                    const timeSinceLastSpin = now - lastSpinTimeRef.current;
                    
                    if (isDevelopment && timeSinceLastSpin < 5000) {
                      return;
                    }
                    
                    lastSpinTimeRef.current = now;
                    
                    // Only record non-game results here, game results are recorded by game cards
                    if (!gameType) {
                      addSpinResult(winnerName, inferredType, spinnerName, gameType);
                    }
                  }
                  
                  if (shouldRemoveParticipant && !isDiceMode) {
                    const id = winnerRef.current && typeof winnerRef.current.id === 'string' ? winnerRef.current.id : '';
                    if (id.length > 0) {
                      const remover = removeActive(id);
                    }
                  }
                  setWinnerIndex(null);
                  setIsCelebrating(false);
                  setRemovingWinner(false);
                  setIsSidebarLocked(false); // Unlock sidebar after celebration
                  slowSpinLastTime.current = performance.now();
                  // Reset Safari-specific guard for next spin
                  winnerAnimationTriggeredRef.current = false;
                }, 1000);
              }, 6000);
            }, 300); // Reduced buffer for more responsive feel
            timeoutRefs.current.bufferTimeout = setTimeout(() => {
              // Buffer phase
              spinnerSettledRef.current = true;
            }, 300);
          }, 100); // Reduced initial buffer
        }
      }
      requestAnimationFrame(animate);

      // Function to handle visibility changes specifically for this spin's sound
      const spinSoundVisibilityHandler = () => {
        if (!spinSoundRef.current) return;
        const spinShouldBeInProgress = (performance.now() - startTime) < totalDuration;
        if (document.hidden) {
          spinSoundRef.current.pause();
        } else if (spinShouldBeInProgress && audioEnabled) {
          spinSoundRef.current.play().catch(e => console.error("Error resuming spin sound:", e));
        }
      };

      if (audioEnabled && spinSoundRef.current) {
        spinSoundRef.current.currentTime = 0;
        spinSoundRef.current.play().catch(e => console.error("Error playing spin sound (initial):", e));
        document.addEventListener('visibilitychange', spinSoundVisibilityHandler);
        
        // Clean up the event listener after the spin duration
        setTimeout(() => {
          document.removeEventListener('visibilitychange', spinSoundVisibilityHandler);
        }, totalDuration + 1000); // Add 1 second buffer
      }
    }, 50); // Small delay to clear any residual state
  };

  const handleSpinWithPointer = () => {
    handleSpin();
  };

  // Red box spinner name display
  const renderSpinnerName = () => (
    <div
      className="z-10 flex items-center"
      style={{
        position: 'absolute',
        top: 8,
        left: 5,
        marginBottom: 0,
        padding: '0 8px',
        background: isDarkMode ? 'rgba(20,22,30,0.98)' : 'rgba(255,255,255,0.85)',
        borderRadius: 8,
        maxWidth: '80%',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
      }}
    >
      {spinnerName ? (
        <>
          <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} mr-2 truncate ${isMobile ? 'text-sm max-w-[120px]' : 'text-lg max-w-[180px]'}`}>{spinnerName}</span>
          <button
            onClick={() => { setPendingName(spinnerName); setShowNameModal(true); }}
            className={`bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center border-none cursor-pointer ${isMobile ? 'w-6 h-6' : 'w-8 h-8'}`}
            aria-label="Edit spinner name"
          >
            <Pencil className="text-gray-600 dark:text-gray-300" size={isMobile ? 14 : 18} />
          </button>
        </>
      ) : (
        <button
          onClick={() => { setPendingName(''); setShowNameModal(true); }}
          className={`bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center border-none cursor-pointer ${isMobile ? 'w-6 h-6' : 'w-8 h-8'}`}
          aria-label="Add spinner name"
        >
          <Pencil className="text-gray-600 dark:text-gray-300" size={isMobile ? 14 : 18} />
        </button>
      )}
    </div>
  );

  // Example:
  // if (sidebarMode === 'games' && currentGameType === 'would-you-rather') { ... }

  // When passing gameType to addSpinResult:
  // const gameType = sidebarMode === 'games' ? currentGameType : undefined;

  return (
    <div style={{ ...parentFlexStyle, overflow: 'auto' }}>
      {/* Spinner Section - At the top */}
      <div className="red-box-container" style={redBoxStyle}>
        {renderSpinnerName()}
        <div className="spinner-container" style={{ width: paddedDimension, height: paddedDimension, position: 'relative', overflow: 'visible', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div className={`relative w-full h-full ${isMobile ? 'mt-8' : ''}`} style={spinnerContainerStyle}>
            <Spinner
              angle={rotation}
              participants={activeList.map(p => {
                // For colors, pass the hex code to the Spinner component
                if (mode === 'colors') {
                  let selectedColorOptions;
                  switch (colorType) {
                    case 'skin':
                      selectedColorOptions = colorSkinOptions;
                      break;
                    case 'eyes':
                      selectedColorOptions = colorEyesOptions;
                      break;
                    case 'hair':
                      selectedColorOptions = colorHairOptions;
                      break;
                    default:
                      selectedColorOptions = colorOptions;
                  }
                  const colorObj = selectedColorOptions.find(c => c.name === p.name);
                  return colorObj ? colorObj.name : p.name;
                }
                return p.name;
              })}
              bottleImg={selectedSpinner.icon || ''}
              onSpin={handleSpinWithPointer}
              spinning={spinning}
              winnerIndex={winnerIndex}
              containerDimension={spinnerDimension}
              spinnerType={selectedSpinner.id}
              colorType={colorType as 'random' | 'skin' | 'eyes'}
            />
            
            {/* Invisible overlay to prevent clicks when card is visible */}
            {showGameCard && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'transparent',
                borderRadius: '50%',
                pointerEvents: 'auto',
                zIndex: 5,
                cursor: 'default'
              }} 
              onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>
        </div>
        
        {/* Would You Rather Game Card - positioned within red box */}
        {/* Game card shows when currentGameType is 'would-you-rather' regardless of sidebar sub-menu */}
        {currentGameType === 'would-you-rather' && (
          <WouldYouRatherGameCard 
            isVisible={showGameCard}
            onComplete={(gameResultCandidate?: string) => {
              setShowGameCard(false);
              setGameCardShouldPersist(false); // Reset persistence when game card is completed
              // Record the result with outcome
              if (gameCardWinnerName && addSpinResult) {
                addSpinResult(gameCardWinnerName, 'participants', spinnerName, currentGameType, gameResultCandidate);
              }
            }}
            spinnerDimension={spinnerDimension}
            winnerName={gameCardWinnerName}
            language={currentLanguage as 'en' | 'es' | 'fr' | 'hi' | 'de'}
          />
        )}
        
        {/* Which One You Are Game Card - positioned within red box */}
        {/* Game card shows when currentGameType is 'which-one-you-are' regardless of sidebar sub-menu */}
        {currentGameType === 'which-one-you-are' && (
          <WhichOneYouAreCard 
            isVisible={showGameCard}
            onComplete={(gameResultCandidate?: string) => {
              setShowGameCard(false);
              setGameCardShouldPersist(false); // Reset persistence when game card is completed
              // Record the result with outcome
              if (gameCardWinnerName && addSpinResult) {
                addSpinResult(gameCardWinnerName, 'participants', spinnerName, currentGameType, gameResultCandidate);
              }
            }}
            spinnerDimension={spinnerDimension}
            winnerName={gameCardWinnerName}
            language={currentLanguage as 'en' | 'es' | 'fr' | 'hi' | 'de'}
          />
        )}
        
        {/* Trivia Game Card - positioned within red box */}
        {/* Game card shows when currentGameType is 'trivia' regardless of sidebar sub-menu */}
        {currentGameType === 'trivia' && (
          <TriviaGameCard 
            isVisible={showGameCard}
            onComplete={(gameResultCandidate?: string) => {
              setShowGameCard(false);
              setGameCardShouldPersist(false); // Reset persistence when game card is completed
              // Record the result with outcome
              if (gameCardWinnerName && addSpinResult) {
                addSpinResult(gameCardWinnerName, 'participants', spinnerName, currentGameType, gameResultCandidate);
              }
            }}
            spinnerDimension={spinnerDimension}
            winnerName={gameCardWinnerName}
            language={currentLanguage as 'en' | 'es' | 'fr' | 'hi' | 'de'}
          />
        )}
        
        {/* Ad Manager - Positioned relative to red box edges */}
        <AdManager />
        
        {/* Truth and Dare Game Card - positioned within red box */}
        {/* Game card shows when currentGameType is 'truth-dare' regardless of sidebar sub-menu */}
        {currentGameType === 'truth-dare' && (
          <TruthDareCard 
            isVisible={showGameCard}
            onComplete={(gameResultCandidate?: string) => {
              setShowGameCard(false);
              setGameCardShouldPersist(false); // Reset persistence when game card is completed
              // Record the result with outcome
              if (gameCardWinnerName && addSpinResult) {
                addSpinResult(gameCardWinnerName, 'participants', spinnerName, currentGameType, gameResultCandidate);
              }
            }}
            spinnerDimension={spinnerDimension}
            winnerName={gameCardWinnerName}
            language={currentLanguage as 'en' | 'es' | 'fr' | 'hi' | 'de'}
          />
        )}
      </div>

      {/* Landing Page Content - Below the spinner */}
      <div className="w-full">
        <LandingPageContent />
      </div>

      {/* Version Display - transparent button above footer, right-aligned */}
      <div
        style={{
          textAlign: 'right',
          padding: '8px 16px',
          fontSize: '12px',
          color: isDarkMode ? '#9CA3AF' : '#6B7280',
          fontFamily: 'monospace',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          transition: 'opacity 0.2s ease',
        }}
        className="version-display"
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '0.7';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '1';
        }}
        onClick={() => {
          // Optional: Add click functionality if needed
          console.log('Version clicked:', getEnvironmentInfo());
        }}
      >
        {getEnvironmentInfo()}
      </div>

      {/* CDN Status Card - Debug overlay - only show when game card is actually visible or explicitly requested */}
      

      {/* Name Modal */}
      {showNameModal && (
        <Modal
          isOpen={showNameModal}
          onClose={() => setShowNameModal(false)}
          title="Set Spinner Name"
        >
          <div className="space-y-4">
            <div>
              <label htmlFor="spinnerName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Spinner Name
              </label>
              <input
                id="spinnerName"
                type="text"
                value={pendingName}
                onChange={(e) => setPendingName(e.target.value)}
                placeholder="Enter spinner name..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                autoFocus
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowNameModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setSpinnerName(pendingName);
                  setShowNameModal(false);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Save
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default MainContentSection;
